/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { Component, Inject, Injector, input, Input, OnInit } from '@angular/core';
import { LINE_ISOLATION_INSTANCE_TOKEN } from '@app/components/isolation/line-isolation-instance.token';
import { IsolationInstanceData } from '@app/types/LineIsolation';
import { LineIsolationService } from './line-isolation.service';
import { ComponentPortal } from '@angular/cdk/portal';
import { LineIsolationListComponent } from './line-isolation-list.component';
import { Observable } from 'rxjs';
import { DashGisOverlay } from '../../core/ui/overlay/dash-gis-overlay.service';
import { LineIsolationListOverlayRef } from './line-isolation-list-overlay-ref';
import { mergeMap, take } from 'rxjs/operators';
import { LoggerService } from '../../modules/shared/services/logger.service';
import { MatIconButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { AsyncPipe } from '@angular/common';

@Component({
    selector: 'dash-gis-line-isolation',
    template: ` @if (isLineIsolationEnabled$ | async) {
        <button mat-icon-button matTooltip="Line Isolation Query" (click)="query()">
            <mat-icon svgIcon="isolation" aria-label="Line Isolation Query"></mat-icon>
        </button>
        }`,
    imports: [MatIconButton, MatTooltip, MatIcon, AsyncPipe],
})
export class LineIsolationComponent implements OnInit {
    layerName = input.required<string>();
    mapName = input.required<string>();

    isLineIsolationEnabled$: Observable<boolean>;

    constructor(
        private logger: LoggerService,
        private lineIsolationService: LineIsolationService,
        private overlay: DashGisOverlay,
        private injector: Injector
    ) {}

    public ngOnInit() {
        this.isLineIsolationEnabled$ = this.lineIsolationService.isIsolationEnabled(this.mapName(), this.layerName());
    }

    public query() {
        this.lineIsolationService
            .getSelectedFeatures(this.mapName(), this.layerName())
            .pipe(
                mergeMap((features) => {
                    if (features.length > 0) {
                        this.lineIsolationService.query(this.mapName(), this.layerName(), features);
                        const overlayRef = this.overlay.create({
                            hasBackdrop: false,
                            panelClass: 'dash-gis-floating-panel',
                            width: '350px',
                            positionStrategy: this.overlay.position().global().centerHorizontally().centerVertically(),
                        });
                        const lineIsolationOverlayRef = new LineIsolationListOverlayRef(overlayRef);
                        const injector = this.createInjector(lineIsolationOverlayRef);
                        const listPortal = new ComponentPortal(LineIsolationListComponent, null, injector);
                        overlayRef.attach(listPortal);
                        return overlayRef.detachments();
                    }
                })
            )
            .subscribe(() => {
                this.lineIsolationService.clearIsolationData();
            });
    }

    private createInjector(overlayRef: LineIsolationListOverlayRef): Injector {
        const instanceData: IsolationInstanceData = {
            mapName: this.mapName(),
            layerName: this.layerName()
        }
        return Injector.create({
            providers: [
                { provide: LineIsolationListOverlayRef, useValue: overlayRef },
                { provide: LINE_ISOLATION_INSTANCE_TOKEN, useValue: instanceData }
            ],
            parent: this.injector,
        });
    }
}
